/**
 * Å komme seg fra et navn på skjermen til noe man kan åpne.
 *
 * Planen navnga ladestasjoner, hoteller, spisesteder og skianlegg, og de fleste
 * av dem var bare tekst. «Recharge Vulkan p-hus» sier ingenting om hvor det er
 * eller om det passer bilen din — og av de 1 534 fasilitetene fra
 * OpenStreetMap har ingen en nettside i det hele tatt.
 *
 * Derfor er regelen her at *alt* skal kunne åpnes. Har stedet en ekte nettside,
 * er det den; har det koordinater, er det kartet; ellers er det et søk. Rekkefølgen
 * er etter hvor nyttig svaret er, ikke etter hva som er lettest å lage.
 *
 * Kartlenka er med vilje et søk på navn *og* koordinater framfor bare et punkt.
 * Et punkt viser hvor det er; et søk viser hva som ligger der — åpningstider,
 * bilder, anmeldelser, telefonnummer. Det er det man er ute etter når man lurer
 * på om man skal kjøre innom.
 *
 * Nettsida er den beste lenka helt til den ikke svarer, og da er den den verste.
 * Adressene er hentet fra OpenStreetMap på et tidspunkt, og et lite hotell som
 * har lagt ned, byttet domene eller står i vedlikeholdsmodus ser nøyaktig
 * likedan ut herfra som ett som tar imot gjester. Vi *kan ikke* sjekke det:
 * nettleseren nektes å lese svaret fra et annet domene, og et `no-cors`-kall
 * gir en ugjennomsiktig respons der 200 og 503 er umulige å skille.
 *
 * Derfor er svaret ikke å gjette, men å alltid ha en vei til. `placeLinks` gir
 * både nettsida og et kartsøk, og grensesnittet viser begge. Nettsida nede blir
 * da ett klikk tilbake, ikke en blindvei.
 */

export type PlaceLinkKind = "website" | "map" | "search";

export interface PlaceLink {
  href: string;
  kind: PlaceLinkKind;
  /** Vertsnavnet uten «www.», når det er en ekte nettside. Til å vise fram. */
  host?: string;
}

export interface Placeable {
  name: string;
  website?: string;
  lat?: number;
  lon?: number;
}

export interface PlaceLinks {
  primary: PlaceLink;
  /**
   * Den andre veien til stedet.
   *
   * Finnes bare når den primære er en nettside, og det er hele poenget: har
   * stedet ingen nettside, *er* den primære allerede kartsøket, og to like
   * lenker ved siden av hverandre er støy. Er den primære en nettside, kan den
   * være død — og da er dette utveien.
   */
  fallback?: PlaceLink;
}

/**
 * Godtar bare http og https.
 *
 * Adressene kommer fra genererte datafiler i dette repoet, ikke fra brukere, så
 * dette er ikke et forsvar mot en angriper. Det er et forsvar mot en dårlig
 * OSM-oppføring: `javascript:` og `data:` i en href er en kjørbar lenke, og en
 * generert fil er nettopp den slags man ikke leser gjennom.
 */
export function safeWebsite(website?: string): { href: string; host: string } | undefined {
  if (!website) return undefined;
  try {
    const url = new URL(website);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    return { href: url.toString(), host: url.hostname.replace(/^www\./, "") };
  } catch {
    return undefined;
  }
}

/** Google Maps, med navnet og punktet. Åpner kartappen på telefon. */
export function mapSearchUrl(name: string, lat?: number, lon?: number): string {
  const query = typeof lat === "number" && typeof lon === "number"
    ? `${name} ${lat},${lon}`
    : name;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** Vanlig nettsøk. Siste utvei, for det som verken har nettside eller punkt. */
export function webSearchUrl(name: string, near?: string): string {
  const query = near ? `${name} ${near}` : name;
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

/**
 * Den beste lenka stedet kan få.
 *
 * `near` er stedsnavnet i nærheten — «Voss», «Eidfjord». Det brukes bare i
 * søket, og det er der det trengs: «Bunnpris» alene finner tre hundre butikker,
 * «Bunnpris Eidfjord» finner én.
 */
export function placeLink(place: Placeable, near?: string): PlaceLink {
  return placeLinks(place, near).primary;
}

/**
 * Den beste lenka, og veien videre når den ikke virker.
 *
 * Se modulkommentaren: vi har ingen måte å vite om en nettside svarer, så i
 * stedet for å velge for brukeren viser vi begge. Kartsøket finner stedet
 * uansett hva som har skjedd med domenet.
 */
export function placeLinks(place: Placeable, near?: string): PlaceLinks {
  const site = safeWebsite(place.website);
  const withoutSite = mapOrSearch(place, near);

  if (!site) return { primary: withoutSite };

  return {
    primary: { href: site.href, kind: "website", host: site.host },
    fallback: withoutSite,
  };
}

/** Stedet uten nettsida: kartet når vi har punktet, ellers et søk. */
function mapOrSearch(place: Placeable, near?: string): PlaceLink {
  if (typeof place.lat === "number" && typeof place.lon === "number") {
    return { href: mapSearchUrl(place.name, place.lat, place.lon), kind: "map" };
  }
  return { href: webSearchUrl(place.name, near), kind: "search" };
}
